export type FormattingSettings = {
  locale: string;
  currency: string;
  timeZone: string;
};

type FormatMoneyOptions = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

type FormatNumberOptions = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

export const DEFAULT_FORMATTING_SETTINGS: FormattingSettings = {
  locale: 'en-US',
  currency: 'USD',
  timeZone: 'UTC',
};

const normalizeFormatting = (settings?: Partial<FormattingSettings>): FormattingSettings => ({
  locale: settings?.locale || DEFAULT_FORMATTING_SETTINGS.locale,
  currency: settings?.currency || DEFAULT_FORMATTING_SETTINGS.currency,
  timeZone: settings?.timeZone || DEFAULT_FORMATTING_SETTINGS.timeZone,
});

type DateStyle = 'short' | 'medium' | 'long';

type FormatDateInTimeZoneOptions = {
  locale: string;
  timeZone: string;
  dateStyle?: DateStyle;
};

const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateInput = (date: Date | string): Date | null => {
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const trimmed = date.trim();
  const dateOnlyMatch = trimmed.match(ISO_DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, yearRaw, monthRaw, dayRaw] = dateOnlyMatch;
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

    // Use UTC noon to prevent date-only strings from shifting to prior day in common negative offsets.
    const parsed = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
    if (Number.isNaN(parsed.getTime())) return null;
    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatMoney = (
  value: number | null | undefined,
  settings?: Partial<FormattingSettings>,
  opts?: FormatMoneyOptions
): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';

  const { locale, currency } = normalizeFormatting(settings);

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...opts,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(DEFAULT_FORMATTING_SETTINGS.locale, {
      style: 'currency',
      currency: DEFAULT_FORMATTING_SETTINGS.currency,
      ...opts,
    }).format(value);
  }
};

export const formatNumber = (
  value: number | null | undefined,
  settings?: Pick<FormattingSettings, 'locale'>,
  opts?: FormatNumberOptions
): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';

  try {
    return new Intl.NumberFormat(settings?.locale || DEFAULT_FORMATTING_SETTINGS.locale, {
      ...opts,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(DEFAULT_FORMATTING_SETTINGS.locale, {
      ...opts,
    }).format(value);
  }
};

export const formatDateInTimeZone = (
  date: Date | string,
  opts: FormatDateInTimeZoneOptions
): string => {
  const parsed = parseDateInput(date);
  if (!parsed) return '—';

  try {
    return new Intl.DateTimeFormat(opts.locale, {
      dateStyle: opts.dateStyle || 'medium',
      timeZone: opts.timeZone,
    }).format(parsed);
  } catch {
    try {
      return new Intl.DateTimeFormat(DEFAULT_FORMATTING_SETTINGS.locale, {
        dateStyle: opts.dateStyle || 'medium',
        timeZone: DEFAULT_FORMATTING_SETTINGS.timeZone,
      }).format(parsed);
    } catch {
      return '—';
    }
  }
};

export const getTodayDateKeyInTimeZone = (timeZone: string): string => {
  const formatter = (() => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }
  })();

  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) return '';
  return `${year}-${month}-${day}`;
};

const getDatePartsInTimeZone = (
  timeZone: string,
  referenceDate: Date = new Date()
): { year: number; month: number; day: number } | null => {
  const formatter = (() => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }
  })();

  const parts = formatter.formatToParts(referenceDate);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
};

const parseDateKeyToUTCNoon = (dateKey: string): Date | null => {
  const match = dateKey.match(DATE_KEY_PATTERN);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const parsed = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const getMonthBoundsDateKeysInTimeZone = (
  timeZone: string,
  referenceDateKey?: string
): { startDateKey: string; endDateKey: string } => {
  const referenceDate = referenceDateKey ? parseDateKeyToUTCNoon(referenceDateKey) : null;
  const parts = getDatePartsInTimeZone(timeZone, referenceDate || new Date());
  if (!parts) return { startDateKey: '', endDateKey: '' };

  const daysInMonth = new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate();
  const month = pad2(parts.month);
  return {
    startDateKey: `${parts.year}-${month}-01`,
    endDateKey: `${parts.year}-${month}-${pad2(daysInMonth)}`,
  };
};
